'use client';

// Report a thread or comment (Reddit-style community moderation signal).
// One report per user per target; the button remembers it was sent.

import { useRef, useState } from 'react';
import { Flag, Check } from 'lucide-react';
import { useLang } from '@/lib/i18n/LanguageContext';

const MENU_WIDTH = 150;

const REASONS: Array<{ key: string; label: string }> = [
  { key: 'SPAM', label: 'Spam' },
  { key: 'ABUSE', label: 'Abusive' },
  { key: 'SCAM', label: 'Scam' },
  { key: 'OTHER', label: 'Other' },
];

export default function ReportButton({ targetType, targetId }: {
  targetType: 'THREAD' | 'COMMENT';
  targetId: string;
}) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [reported, setReported] = useState(false);
  // Fixed positioning so ancestors with overflow-hidden can't clip the menu
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  function toggle() {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({
        top: Math.min(r.bottom + 6, window.innerHeight - 170),
        left: Math.max(8, Math.min(r.left, window.innerWidth - MENU_WIDTH - 8)),
      });
    }
    setOpen((o) => !o);
  }

  async function report(reason: string) {
    setOpen(false);
    setReported(true); // optimistic — repeat reports are a server-side no-op anyway
    try {
      await fetch('/api/forum/report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetType, targetId, reason }),
      });
    } catch {}
  }

  if (reported) {
    return (
      <span className="flex items-center gap-1 text-xs" style={{ color: '#4d5a6b' }}>
        <Check size={12} />
        {t('Reported')}
      </span>
    );
  }

  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        onClick={toggle}
        className="flex items-center gap-1 text-xs"
        style={{ color: open ? '#f59e0b' : '#6b7280' }}
      >
        <Flag size={12} />
        {t('Report')}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div
            className="fixed z-[61] rounded-xl py-1.5 animate-fade-in"
            style={{ top: pos.top, left: pos.left, width: MENU_WIDTH, background: '#1a2030', border: '1px solid #2c3547', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}
          >
            {REASONS.map((r) => (
              <button
                key={r.key}
                onClick={() => report(r.key)}
                className="w-full text-left px-3.5 py-2 text-xs font-medium transition-colors hover:bg-white/5"
                style={{ color: '#cbd5e1' }}
              >
                {t(r.label)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
